import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaSearch, FaHistory, FaFire, FaChevronDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { 
  performEnhancedSearch, 
  getSearchSuggestions, 
  getPopularSearches,
  highlightSearchTerms,
  getSearchResultIcon
} from '../../utils/searchUtils';
import { searchCategories } from '../../data/general/searchIndex';
import '../../css/ui/SearchOverlay.css';

const SearchOverlay = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showCategories, setShowCategories] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Save recent searches to localStorage
  const saveRecentSearch = (searchTerm) => {
    const updated = [searchTerm, ...recentSearches.filter(s => s !== searchTerm)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Enhanced search function
  const performSearch = async (searchQuery, category = 'All') => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    
    try {
      // Simulate a small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const searchResults = performEnhancedSearch(searchQuery, category);
      setResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Get search suggestions
  const getSuggestions = (searchQuery) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    
    const searchSuggestions = getSearchSuggestions(searchQuery);
    setSuggestions(searchSuggestions);
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim()) {
        performSearch(query, selectedCategory);
        getSuggestions(query);
      } else {
        setResults([]);
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, selectedCategory]);

  // Handle result click
  const handleResultClick = (result) => {
    saveRecentSearch(query);
    navigate(result.path);
    onClose();
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  // Handle recent search click
  const handleRecentSearchClick = (searchTerm) => {
    setQuery(searchTerm);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  // Handle popular search click - FIXED: Now actually performs the search
  const handlePopularSearchClick = (searchTerm) => {
    setQuery(searchTerm);
    setShowSuggestions(false);
    // Actually perform the search immediately
    performSearch(searchTerm, selectedCategory);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const maxIndex = Math.max(suggestions.length - 1, results.length - 1);
      setHighlightedIndex(prev => prev < maxIndex ? prev + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const maxIndex = Math.max(suggestions.length - 1, results.length - 1);
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : maxIndex);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        if (highlightedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        } else {
          const resultIndex = highlightedIndex - suggestions.length;
          if (results[resultIndex]) {
            handleResultClick(results[resultIndex]);
          }
        }
      } else if (query.trim()) {
        performSearch(query, selectedCategory);
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Reset highlighted index when query changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [query]);

  // Close suggestions when clicking outside - FIXED: More specific click handling
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on search elements
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        // Only close suggestions, not the entire modal
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const popularSearches = getPopularSearches();

  return (
    <div className="search-overlay" onClick={onClose}>
      <div className="search-box" onClick={e => e.stopPropagation()}>
        <FaSearch className="search-icon" />
        <input
          ref={inputRef}
          className="search-input"
          autoFocus
          placeholder="Search the entire Hopkins Hillel website..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
        />
        
        {/* Category Filter */}
        <div className="search-category-filter">
          <button
            className="category-filter-button"
            onClick={() => setShowCategories(!showCategories)}
          >
            <span>{selectedCategory}</span>
            <FaChevronDown className={`category-arrow ${showCategories ? 'open' : ''}`} />
          </button>
          
          {showCategories && (
            <div className="category-dropdown">
              {searchCategories.map(category => (
                <button
                  key={category}
                  className={`category-option ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedCategory(category);
                    setShowCategories(false);
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          )}
        </div>

        <button className="search-close-button" onClick={onClose} aria-label="Close search overlay">
          <FaTimes />
        </button>
      </div>
      
      {/* Search Suggestions and Results */}
      <div className="search-content" ref={suggestionsRef} onClick={e => e.stopPropagation()}>
        {/* Show suggestions when input is focused and has content */}
        {showSuggestions && query.length > 0 && (
          <div className="search-suggestions">
            {suggestions.length > 0 && (
              <div className="suggestions-section">
                <h4>Suggestions</h4>
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className={`suggestion-item ${highlightedIndex === index ? 'highlighted' : ''}`}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <FaSearch className="suggestion-icon" />
                    <span>{suggestion}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Show recent searches when no query */}
        {!query && (
          <div className="search-suggestions">
            {recentSearches.length > 0 && (
              <div className="suggestions-section">
                <h4><FaHistory /> Recent Searches</h4>
                {recentSearches.map((searchTerm, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleRecentSearchClick(searchTerm)}
                  >
                    <FaHistory className="suggestion-icon" />
                    <span>{searchTerm}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="suggestions-section">
              <h4><FaFire /> Popular Searches</h4>
              <div className="popular-searches">
                {popularSearches.map((searchTerm, index) => (
                  <button
                    key={index}
                    className="popular-search-tag"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent modal from closing
                      handlePopularSearchClick(searchTerm);
                    }}
                  >
                    {searchTerm}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {query.length > 0 && (
          <div className="search-results">
            {isSearching ? (
              <div className="search-loading">
                <div className="loading-spinner"></div>
                <span>Searching...</span>
              </div>
            ) : results.length > 0 ? (
              <>
                <div className="results-header">
                  <span className="results-count">{results.length} results found</span>
                  {selectedCategory !== 'All' && (
                    <span className="category-filter-tag">Filtered by: {selectedCategory}</span>
                  )}
                </div>
                
                {results.map((result, index) => {
                  const resultIndex = suggestions.length + index;
                  return (
                    <div 
                      key={result.id} 
                      className={`search-result-card ${highlightedIndex === resultIndex ? 'highlighted' : ''}`}
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="search-result-icon">
                        {getSearchResultIcon(result.type)}
                      </div>
                      <div className="search-result-content">
                        <div 
                          className="search-result-title"
                          dangerouslySetInnerHTML={{
                            __html: highlightSearchTerms(result.title, query)
                          }}
                        />
                        <div className="search-result-subtitle">{result.subtitle}</div>
                        <div 
                          className="search-result-description"
                          dangerouslySetInnerHTML={{
                            __html: highlightSearchTerms(result.description, query)
                          }}
                        />
                        <div className="search-result-meta">
                          <span className="result-category">{result.category}</span>
                          <span className="result-type">{result.type.replace('-', ' ')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="search-no-results">
                <div className="search-no-results-text">No results found for "{query}"</div>
                <div className="search-no-results-suggestions">
                  <p>Try:</p>
                  <ul>
                    <li>Checking your spelling</li>
                    <li>Using different keywords</li>
                    <li>Using more general terms</li>
                    <li>Browsing by category using the filter above</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchOverlay; 